import { BX } from '../internal/global';
import { ajax } from './ajax';

/**
 * Controller calls: `BX.ajax.runAction` and `BX.ajax.runComponentAction`.
 *
 * More than ten modules match on the shapes these produce - the `NETWORK_ERROR` envelope, a
 * rejection carrying the server's own envelope, the single retry with a fresh token on
 * `invalid_csrf` - so they are transcribed as they were; `test/old/core/ajax-actions.test.ts` pins
 * them. The chain mixes `BX.Promise` (synchronous, from `BX.ajax.promise`) with a native Promise
 * around the asset loading, and that mix is part of the timing callers see.
 */

/**
 * @see isValidAnalyticsData in ui.analytics
 */
const isValidAnalyticsData = function(analytics: any): boolean
{
	if (!BX.Type.isPlainObject(analytics))
	{
		console.error('BX.ajax: {analytics} must be an object.');

		return false;
	}

	const requiredFields = ['event', 'tool', 'category'];
	for (const field of requiredFields)
	{
		if (!BX.Type.isStringFilled(analytics[field]))
		{
			console.error(`BX.ajax: The "${field}" property in the "analytics" object must be a non-empty string.`);

			return false;
		}
	}

	const additionalFields = ['p1', 'p2', 'p3', 'p4', 'p5'];
	for (const field of additionalFields)
	{
		const value = analytics[field];
		if (!BX.Type.isStringFilled(value))
		{
			continue;
		}

		if (value.split('_').length > 2)
		{
			console.error(`BX.ajax: The "${field}" property (${value}) in the "analytics" object must be a string containing a single underscore.`);

			return false;
		}
	}

	return true;
};

const processAnalyticsDataToGetParameters = function(config: any): Record<string, any>
{
	const getParameters: Record<string, any> = {};
	if (BX.Type.isStringFilled(config.analyticsLabel) || BX.Type.isPlainObject(config.analyticsLabel))
	{
		getParameters.analyticsLabel = config.analyticsLabel;
	}

	if (BX.Type.isPlainObject(config.analytics))
	{
		if (config.analyticsLabel)
		{
			delete getParameters.analyticsLabel;
			console.error('BX.ajax: Only {analytics} or {analyticsLabel} should be used. If both are present, {analyticsLabel} will be ignored.');
		}

		if (isValidAnalyticsData(config.analytics))
		{
			getParameters.st = config.analytics;
		}
		else
		{
			console.error('BX.ajax: {analytics} is invalid and is skipped.');
		}
	}

	return getParameters;
};

const prepareAjaxGetParameters = function(config: any): Record<string, any>
{
	let getParameters = config.getParameters || {};
	getParameters = { ...getParameters, ...processAnalyticsDataToGetParameters(config) };

	if (typeof config.mode !== 'undefined')
	{
		getParameters.mode = config.mode;
	}

	if (config.navigation)
	{
		if (config.navigation.page)
		{
			getParameters.nav = 'page-' + config.navigation.page;
		}

		// eslint-disable-next-line unicorn/explicit-length-check -- `size` is the page size, a number
		if (config.navigation.size)
		{
			if (getParameters.nav)
			{
				getParameters.nav += '-';
			}
			else
			{
				getParameters.nav = '';
			}
			getParameters.nav += 'size-' + config.navigation.size;
		}
	}

	return getParameters;
};

const prepareAjaxConfig = function(config: any): any
{
	config = BX.type.isPlainObject(config) ? config : {};

	config.headers = config.headers || [];
	config.headers.push({ name: 'X-Bitrix-Csrf-Token', value: BX.bitrix_sessid() });
	if (BX.message.SITE_ID)
	{
		config.headers.push({ name: 'X-Bitrix-Site-Id', value: BX.message.SITE_ID });
	}

	if (typeof config.json !== 'undefined')
	{
		if (!BX.type.isPlainObject(config.json))
		{
			// eslint-disable-next-line unicorn/prefer-type-error -- callers see this class, it stays as it was
			throw new Error('Wrong `config.json`, plain object expected.');
		}

		config.headers.push({ name: 'Content-Type', value: 'application/json' });
		config.data = config.json;
		config.preparePost = false;
	}
	else if (config.data instanceof FormData)
	{
		config.preparePost = false;
		if (typeof config.signedParameters !== 'undefined')
		{
			config.data.append('signedParameters', config.signedParameters);
		}
	}
	else if (BX.type.isPlainObject(config.data) || BX.Type.isNil(config.data))
	{
		config.data = BX.type.isPlainObject(config.data) ? config.data : {};
		if (typeof config.signedParameters !== 'undefined')
		{
			config.data.signedParameters = config.signedParameters;
		}
	}

	if (!config.method)
	{
		config.method = 'POST';
	}

	return config;
};

const buildAjaxPromiseToRestoreCsrf = function(config: any, withoutRestoringCsrf?: any): any
{
	withoutRestoringCsrf = withoutRestoringCsrf || false;
	const originalConfig = BX.clone(config);
	let request: any = null;

	const onrequeststart = config.onrequeststart;
	config.onrequeststart = function(xhr: any): void
	{
		request = xhr;
		if (BX.type.isFunction(onrequeststart))
		{
			onrequeststart(xhr);
		}
	};

	const onrequeststartOrig = originalConfig.onrequeststart;
	originalConfig.onrequeststart = function(xhr: any): void
	{
		request = xhr;
		if (BX.type.isFunction(onrequeststartOrig))
		{
			onrequeststartOrig(xhr);
		}
	};

	const promise = BX.ajax.promise(config);

	return promise.then((response: any) => {
		if (!withoutRestoringCsrf && BX.type.isPlainObject(response) && BX.type.isArray(response.errors))
		{
			let csrfProblem = false;
			response.errors.forEach((error: any) => {
				if (error.code === 'invalid_csrf' && error.customData.csrf)
				{
					BX.message({ bitrix_sessid: error.customData.csrf });

					originalConfig.headers = originalConfig.headers || [];
					originalConfig.headers = originalConfig.headers.filter((header: any) => {
						return header && header.name !== 'X-Bitrix-Csrf-Token';
					});
					originalConfig.headers.push({ name: 'X-Bitrix-Csrf-Token', value: BX.bitrix_sessid() });

					csrfProblem = true;
				}
			});

			if (csrfProblem)
			{
				return buildAjaxPromiseToRestoreCsrf(originalConfig, true);
			}
		}

		if (!BX.type.isPlainObject(response) || response.status !== 'success')
		{
			const errorPromise = new BX.Promise();
			errorPromise.reject(response);

			return errorPromise;
		}

		return response;
	}).catch((data: any) => {
		const ajaxReject = new BX.Promise();

		let originalJsonResponse;
		if (BX.type.isPlainObject(data) && data.xhr && data.xhr.responseText)
		{
			try
			{
				originalJsonResponse = JSON.parse(data.xhr.responseText);
				data = originalJsonResponse;
			}
			catch (err)
			{}
		}

		if (BX.type.isPlainObject(data) && data.status && data.hasOwnProperty('data'))
		{
			ajaxReject.reject(data);
		}
		else
		{
			ajaxReject.reject({
				status: 'error',
				data: {
					ajaxRejectData: data,
				},
				errors: [
					{
						code: 'NETWORK_ERROR',
						message: 'Network error',
					},
				],
			});
		}

		return ajaxReject;
	}).then((response: any) => {
		const assetsLoaded = new BX.Promise();

		const headers = request.getAllResponseHeaders().trim().split(/[\r\n]+/);
		const headerMap: Record<string, string> = {};
		headers.forEach((line: string) => {
			const parts: any[] = line.split(': ');
			const header = parts.shift().toLowerCase();
			headerMap[header] = parts.join(': ');
		});

		if (!headerMap['x-process-assets'])
		{
			assetsLoaded.fulfill(response);

			return assetsLoaded;
		}

		const assets = BX.prop.getObject(BX.prop.getObject(response, 'data', {}), 'assets', {});

		const inlineScripts: any[] = [];
		if (BX.Type.isArrayFilled(assets.string))
		{
			assets.string
				.reduce((acc: any[], item: any) => {
					if (String(item).length > 0 && !acc.includes(item))
					{
						acc.push(item);
					}

					return acc;
				}, [])
				.forEach((item: any) => {
					if (String(item).startsWith('<script type="extension/settings"'))
					{
						BX.html(document.head, item, { useAdjacentHTML: true });
					}
					else
					{
						inlineScripts.push(item);
					}
				})
			;
		}

		const assetsPromise = new Promise((resolve) => {
			const css = BX.prop.getArray(assets, 'css', []);
			BX.load(css, () => {
				BX.loadScript(
					BX.prop.getArray(assets, 'js', []),
					resolve,
				);
			});
		});

		// eslint-disable-next-line promise/catch-or-return -- a failed loader leaves the chain pending, as before
		assetsPromise.then(() => {
			const stringAsset = inlineScripts.join('\n');
			// eslint-disable-next-line promise/catch-or-return, promise/no-nesting -- see above
			BX.html(document.head, stringAsset, { useAdjacentHTML: true }).then(() => {
				assetsLoaded.fulfill(response);
			});
		});

		return assetsLoaded;
	});
};

/**
 *
 * @param {string} action
 * @param {Object} config
 * @param {?string|?Object} [config.analyticsLabel]
 * @param {?Object} [config.analytics]
 * @param {string} [config.analytics.event]
 * @param {string} [config.analytics.tool]
 * @param {string} [config.analytics.category]
 * @param {?string} [config.analytics.c_section]
 * @param {?string} [config.analytics.c_sub_section]
 * @param {?string} [config.analytics.c_element]
 * @param {?string} [config.analytics.type]
 * @param {?string} [config.analytics.p1]
 * @param {?string} [config.analytics.p2]
 * @param {?string} [config.analytics.p3]
 * @param {?string} [config.analytics.p4]
 * @param {?string} [config.analytics.p5]
 * @param {?('success' | 'error' | 'attempt' | 'cancel')} [config.analytics.status]
 * @param {string} [config.method='POST']
 * @param {Object} [config.data]
 * @param {?Object} [config.getParameters]
 * @param {?Object} [config.headers]
 * @param {?Object} [config.timeout]
 * @param {Object} [config.navigation]
 * @param {number} [config.navigation.page]
 */
ajax.runAction = function(action: any, config?: any): any
{
	config = prepareAjaxConfig(config);
	const getParameters = prepareAjaxGetParameters(config);
	getParameters.action = action;

	const url = '/bitrix/services/main/ajax.php?' + BX.ajax.prepareData(getParameters);

	return buildAjaxPromiseToRestoreCsrf({
		method: config.method,
		dataType: 'json',
		url,
		data: config.data,
		timeout: config.timeout,
		preparePost: config.preparePost,
		headers: config.headers,
		onrequeststart: config.onrequeststart,
		onprogress: config.onprogress,
		onprogressupload: config.onprogressupload,
	});
};

/**
 *
 * @param {string} component
 * @param {string} action
 * @param {Object} config
 * @param {?string|?Object} [config.analyticsLabel]
 * @param {?Object} [config.analytics]
 * @param {string} [config.analytics.event]
 * @param {string} [config.analytics.tool]
 * @param {string} [config.analytics.category]
 * @param {?string} [config.analytics.c_section]
 * @param {?string} [config.analytics.c_sub_section]
 * @param {?string} [config.analytics.c_element]
 * @param {?string} [config.analytics.type]
 * @param {?string} [config.analytics.p1]
 * @param {?string} [config.analytics.p2]
 * @param {?string} [config.analytics.p3]
 * @param {?string} [config.analytics.p4]
 * @param {?string} [config.analytics.p5]
 * @param {?string} [config.signedParameters]
 * @param {string} [config.method='POST']
 * @param {string} [config.mode='ajax'] Ajax or class.
 * @param {Object} [config.data]
 * @param {?Object} [config.getParameters]
 * @param {?array} [config.headers]
 * @param {?number} [config.timeout]
 * @param {Object} [config.navigation]
 */
ajax.runComponentAction = function(component: any, action: any, config?: any): any
{
	config = prepareAjaxConfig(config);
	config.mode = config.mode || 'ajax';

	const getParameters = prepareAjaxGetParameters(config);
	getParameters.c = component;
	getParameters.action = action;

	const url = '/bitrix/services/main/ajax.php?' + BX.ajax.prepareData(getParameters);

	return buildAjaxPromiseToRestoreCsrf({
		method: config.method,
		dataType: 'json',
		url,
		data: config.data,
		timeout: config.timeout,
		preparePost: config.preparePost,
		headers: config.headers,
		onrequeststart: (config.onrequeststart ? config.onrequeststart : null),
		onprogress: config.onprogress,
		onprogressupload: config.onprogressupload,
	});
};
